import { apiClient } from "./apiClient";

// honeyApi (GET/POST /honey/inventory) removed with V042 (#218). Its last
// consumer, HoneyInventoryPage, is gone; poolApi below is the replacement.

export const clientsApi = {
  list: (params) => apiClient.get("/clients", { params }).then((r) => r.data),
  create: (payload) => apiClient.post("/clients", payload).then((r) => r.data),
  /* Correct a vendor, or put one away (#484).
   *
   * PARTIAL: only the fields passed are written, and an explicit null
   * CLEARS one -- which is how a wrong phone number gets removed rather
   * than being permanent. Sending the whole object back would overwrite
   * the fields this screen does not show. */
  update: (clientId, changes) =>
    apiClient.put(`/clients/${clientId}`, changes).then((r) => r.data),
  /* Fold one vendor into another (#484 follow-up).
   *
   * The vendor in the path is the one PUT AWAY; intoClientId survives.
   * An ID rather than a name, unlike the option lists' merge: those
   * consumers store the TEXT, so a rename there IS a merge. These store
   * client_id, so the two are genuinely different operations and a
   * merge is only ever between two rows that both exist. */
  merge: (clientId, intoClientId) =>
    apiClient.post(`/clients/${clientId}/merge`, { into_client_id: intoClientId })
      .then((r) => r.data),
  transactions: (clientId, { limit, offset } = {}) =>
    apiClient.get(`/clients/${clientId}/transactions`, { params: { limit, offset } })
      .then((r) => r.data),
  addTransaction: (clientId, payload) => apiClient.post(`/clients/${clientId}/transactions`, payload).then((r) => r.data),
  listRecurringOrders: () => apiClient.get("/clients/recurring-orders").then((r) => r.data),
  createRecurringOrder: (payload) => apiClient.post("/clients/recurring-orders", payload).then((r) => r.data),

  // Delivering what a standing order asks for (#253). Writes an ordinary
  // honey assignment, so the delivery lands on the vendor's ledger like
  // any other -- the order is a reminder of a delivery to make, not a
  // second kind of delivery.
  fulfilRecurringOrder: (orderId, payload) =>
    apiClient.post(`/clients/recurring-orders/${orderId}/fulfil`, payload || {}).then((r) => r.data),
  stopRecurringOrder: (orderId) =>
    apiClient.delete(`/clients/recurring-orders/${orderId}`).then((r) => r.data),
};

export const expendituresApi = {
  list: (params) => apiClient.get("/expenditures", { params }).then((r) => r.data),
  create: (payload) => apiClient.post("/expenditures", payload).then((r) => r.data),
};

export const businessAnalyticsApi = {
  pnl: (year) => apiClient.get("/business/pnl", { params: { year } }).then((r) => r.data),
  breakEven: () => apiClient.get("/business/break-even").then((r) => r.data),
  forecast: () => apiClient.get("/business/forecast").then((r) => r.data),
};

/** Honey pool + bottling (#191).
 *
 * The pool is a ledger server-side, so every write returns the recomputed
 * balance. Callers should use THAT rather than adjusting a local number --
 * the server is the only thing that knows about a correction someone made
 * on another device.
 */
export const poolApi = {
  get: () => apiClient.get("/honey/pool").then((r) => r.data),
  /* ONE PRODUCT'S LEDGER (#495). Omitting the argument asks for honey,
     which is what every existing caller meant -- the endpoint had no
     filter at all, so the honey ledger had been showing wax and pollen
     movements since V120 gave them a product. */
  movements: (productType) =>
    apiClient
      .get("/honey/pool/movements", productType ? { params: { product_type: productType } } : undefined)
      .then((r) => r.data),
  // varietalId is optional at EVERY tier (#225). Omitted, the server
  // records the account's "General" varietal -- the column is always
  // written, only the picker is gated, so a later downgrade never flattens
  // honey somebody already separated.
  /*
   * productType since #430. Omitted means Honey, which is what every
   * existing caller means. It travels LAST so the honey callers above --
   * three of them, each already carrying an optional varietal -- did not
   * have to be rewritten to say the thing they have always meant.
   *
   * varietal and product are mutually exclusive in practice: the server
   * resolves a varietal only for honey, and a wax screen never offers
   * one. Not enforced here, because the server is the thing that has to
   * be right about it and duplicating the rule is how the two drift.
   */
  add: (pounds, reason, varietalId, productType) =>
    apiClient
      .post("/honey/pool/add", {
        pounds, reason,
        varietal_id: varietalId || undefined,
        ...(productType ? { product_type: productType } : {}),
      })
      .then((r) => r.data),
  // `pounds` here is the MEASURED total, not a delta -- the server works
  // out the difference. See functions/honey.py correct_pool.
  // varietal_id was missing here as well as on the server (25 Aug), so a
  // correction had no way to say which honey it applied to even once the
  // form offered the choice. Omitted still means the account default,
  // which is what PoolAdjustRequest has always documented.
  correct: (pounds, reason, varietalId, productType) =>
    apiClient
      .post("/honey/pool/correct", {
        pounds,
        reason,
        ...(varietalId ? { varietal_id: Number(varietalId) } : {}),
        ...(productType ? { product_type: productType } : {}),
      })
      .then((r) => r.data),
  // Honey leaving the pool without being bottled (#247). Free accounts have
  // no bottling, so without this their pool only ever grows.
  //
  // `disposition` is passed through with no default on purpose: sold honey
  // left as product and lost honey did not, and the server refuses anything
  // that is not one of the two rather than guessing into an append-only
  // ledger.
  remove: (pounds, disposition, reason, varietalId, productType) =>
    apiClient
      .post("/honey/pool/remove", {
        pounds,
        disposition,
        reason,
        varietal_id: varietalId || undefined,
        ...(productType ? { product_type: productType } : {}),
      })
      .then((r) => r.data),
  // acknowledgeShortfall is set ONLY by the user pressing confirm in the
  // over-draw dialog (#237). Never on a retry: a client that re-posted by
  // itself would authorise the over-draw on their behalf, and a mistyped
  // count is far likelier than a genuine one. A shortfall comes back as a
  // 200 carrying needs_confirmation -- a question, not an error, so it must
  // not be routed through the error path.
  bottle: (bottleSizeId, bottleCount, reason, varietalId, acknowledgeShortfall) =>
    apiClient
      .post("/honey/pool/bottle", {
        bottle_size_id: bottleSizeId,
        bottle_count: bottleCount,
        reason,
        varietal_id: varietalId || undefined,
        acknowledge_shortfall: acknowledgeShortfall || undefined,
      })
      .then((r) => r.data),
  // The inverse: empty filled jars back into the pool to re-fill in another
  // size (#220). Its own call rather than a negative bottle count -- see
  // functions/honey.py unbottle_honey for why.
  // varietalId says which honey is going BACK into the pool. Without it the
  // by-varietal breakdown subtracts from "General" and leaves the varietal
  // that was actually emptied reading full.
  unbottle: (bottleSizeId, bottleCount, reason, varietalId) =>
    apiClient.post("/honey/pool/unbottle", {
      bottle_size_id: bottleSizeId, bottle_count: bottleCount, reason,
      varietal_id: varietalId || undefined,
    }).then((r) => r.data),

  // The season-end review list: every bottling that went beyond recorded
  // stock (#237). A filter over a stored flag, not a replay of the ledger.
  // Lives on poolApi rather than honeyStockApi because it is HoneyChain
  // like the bottling it reports on, not Business like glass.
  listExceeded: () => apiClient.get("/honey/pool/exceeded").then((r) => r.data),

  // includeRetired is for the MANAGE panel only (#410). The pickers must
  // keep getting active sizes only, or a size somebody put away starts
  // being offered again by inheritance.
  /*
   * productType since #430. Omitted means Honey, which is what every
   * existing caller means -- the pool page, the bottling form, the
   * pickers. A wax screen passes "Wax" and gets wax's own sizes, because
   * the label is only unique WITHIN a product: a 4 oz bar and a 4 oz jar
   * are different things that share a name.
   */
  listSizes: (includeRetired, productType) => apiClient
    .get("/honey/bottle-sizes", {
      params: {
        ...(includeRetired ? { include_retired: "true" } : {}),
        ...(productType && productType !== "Honey" ? { product_type: productType } : {}),
      },
    })
    .then((r) => r.data),
  createSize: (label, pounds, productType, defaultPrice) => apiClient
    .post("/honey/bottle-sizes", {
      label,
      pounds,
      ...(defaultPrice != null ? { default_price: defaultPrice } : {}),
      ...(productType && productType !== "Honey" ? { product_type: productType } : {}),
    })
    .then((r) => r.data),
  /*
   * NO retireSize. Putting a size away goes through updateSize now
   * (#410), because the DELETE route can only retire -- it has no way
   * back, which is exactly the one-way trap this issue was about. Two
   * client methods for one gesture, one of them a dead end, is how
   * somebody wires up the wrong one later. The route itself stays;
   * nothing in either app calls it.
   */
  /*
   * Rename a jar size, or bring one back (#410).
   *
   * Reported from a device: "there is no 'Rename' button for the Jar
   * sizes." The list could grow and could not be corrected.
   *
   * `changes` carries label and/or is_active and NEVER pounds -- the
   * pool is kept in pounds and every past bottling subtracted this
   * size's weight per jar at the time, so editing it would change what
   * last season's rows appear to mean rather than recomputing anything.
   * The server has no field for it either.
   */
  updateSize: (id, changes) => apiClient.put(`/honey/bottle-sizes/${id}`, changes).then((r) => r.data),
};

/** Vendors, honey out and money in (#192).
 *
 * "Vendor" is an app.Clients row -- clientsApi manages the records
 * themselves; this covers what goes out to them and what comes back. See
 * V038 for why there is no separate vendor table.
 */
export const vendorApi = {
  balances: () => apiClient.get("/honey/vendors/balances").then((r) => r.data),
  // Vendors holding stock who have gone quiet -- the dashboard block.
  // Filtered, not an inventory list: a shop that took jars last week is
  // fine, one that took them in June and paid nothing is not.
  needsChasing: () => apiClient.get("/honey/vendors/needs-chasing").then((r) => r.data),
  assignments: () => apiClient.get("/honey/assignments").then((r) => r.data),
  // Everything about one vendor: stock held, orders, returns, last payment.
  detail: (clientId) => apiClient.get(`/honey/vendors/${clientId}`).then((r) => r.data),
  // Jars or bulk coming back. condition 'Damaged' clears the debt without
  // returning the honey to stock (#214).
  accept: (payload) => apiClient.post("/honey/returns", payload).then((r) => r.data),
  // The vendor SOLD it, so it leaves their shelf (#257). Carries no price:
  // the debt was created when the honey was assigned, so a sale must not
  // move money or the same honey is billed twice.
  recordSale: (payload) => apiClient.post("/honey/sales", payload).then((r) => r.data),
  // EMPTY jars coming back after the honey sold (#226). Deliberately a
  // different call from accept(): that one credits the vendor's debt and
  // restocks good jars, this one does neither. Logging glass through
  // accept() would wipe a debt the vendor still owes.
  glassReturns: () => apiClient.get("/honey/glass-returns").then((r) => r.data),
  recordGlass: (payload) => apiClient.post("/honey/glass-returns", payload).then((r) => r.data),
  assign: (payload) => apiClient.post("/honey/assignments", payload).then((r) => r.data),
  // assignment_id is optional: a payment need not name the delivery it
  // settles, because in real life it often cannot.
  pay: (payload) => apiClient.post("/honey/payments", payload).then((r) => r.data),
};

// Varietals (#225) and glass (#229).
//
// listVarietals is available at EVERY tier, because every tier writes a
// varietal and so needs to render a name against honey it already has.
// byVarietal and the glass calls are Business-only -- the UI hides them and
// the API refuses them, independently of each other.
export const honeyStockApi = {
  listVarietals: () => apiClient.get("/honey/varietals").then((r) => r.data),
  // Creating is Business-gated while READING is not: every tier renders a
  // varietal name against honey it already has, but separating honey into
  // streams is the Business promise (#249). Idempotent server-side, so a
  // retry on a bad connection cannot make two Clovers.
  createVarietal: (name) => apiClient.post("/honey/varietals", { name }).then((r) => r.data),
  /*
   * Rename a varietal, archive it, or bring it back (#358).
   *
   * One call for both gestures because they edit one row, and the server
   * treats an absent field as "not part of this request" -- so a rename
   * must not send is_active and an archive must not send name. Spreading
   * only what the caller passed is what keeps that true through the
   * client.
   */
  updateVarietal: (varietalId, changes) =>
    apiClient.put(`/honey/varietals/${varietalId}`, changes).then((r) => r.data),
  byVarietal: () => apiClient.get("/honey/pool/by-varietal").then((r) => r.data),

  // The usual price for a size and varietal (V081). A DEFAULT only --
  // every transaction records its own unit_price when it happens, and
  // receipts freeze what was charged. Prices change; past sales must not.
  bottlePrices: () => apiClient.get("/honey/bottle-prices").then((r) => r.data),
  setBottlePrice: (payload) =>
    apiClient.post("/honey/bottle-prices", payload).then((r) => r.data),
  forgetBottlePrice: (bottleSizeId, varietalId) =>
    apiClient.delete(`/honey/bottle-prices/${bottleSizeId}/${varietalId}`).then((r) => r.data),
  listGlass: () => apiClient.get("/honey/glass").then((r) => r.data),
  /*
   * Refuse a bottling run bigger than the clean count (#360).
   *
   * OPT-IN, and off by default, because the default is the considered
   * position rather than an absence: bottling records what already
   * happened, so a count that disagrees is usually the count being wrong.
   * This is for an operation running to a PLAN, which wants telling
   * before it finds out at the filler.
   *
   * Lives on the glass panel rather than in Settings, because it is only
   * meaningful next to the counts it enforces against -- and this is the
   * screen somebody is looking at when they wonder whether the app will
   * stop them.
   */
  jarEnforcement: () => apiClient.get("/honey/glass/enforcement").then((r) => r.data),
  setJarEnforcement: (on) => apiClient
    .put("/honey/glass/enforcement", { enforce_jar_count: on })
    .then((r) => r.data),
  // One call for "start counting" and "correct the count": they are the
  // same act -- telling the app what is on the shelf right now.
  trackGlass: (payload) => apiClient.post("/honey/glass", payload).then((r) => r.data),
  washGlass: (bottleSizeId, count) =>
    apiClient
      .post("/honey/glass/wash", { bottle_size_id: bottleSizeId, count })
      .then((r) => r.data),
};

// Receipts (#193). A document the beekeeper CAN hand over at the point of
// sale if asked, and which stays on the vendor's record either way.
//
// Separate from vendorApi because a receipt is not a vendor operation: it
// renders a sale that already happened, and issuing one never changes the
// ledger.
export const receiptApi = {
  // Takes an assignment_id or a payment_id -- never an amount. The figures
  // come from the recorded sale, so a receipt cannot disagree with the books.
  issue: (payload) => apiClient.post("/honey/receipts", payload).then((r) => r.data),
  // `number` matches EITHER name (#270) -- the official R-YYYY-NNNN or the
  // provisional T- reference a phone printed with no signal. One search,
  // because whoever rings up does not know which kind theirs is.
  list: (clientId, number) =>
    apiClient
      .get("/honey/receipts", {
        params: {
          ...(clientId ? { client_id: clientId } : {}),
          ...(number ? { number } : {}),
        },
      })
      .then((r) => r.data),
  // AFTER the fact, and optional. Showing it on screen or handing over a
  // printout leaves nothing to record -- "issued but not sent" is a
  // complete outcome, not a missing step.
  markSent: (receiptId, payload) =>
    apiClient.post(`/honey/receipts/${receiptId}/sent`, payload).then((r) => r.data),
};

/*
 * The other products (#430 slice 2).
 *
 * ONE CALL for every non-honey product the account has -- its pool
 * balance and its shelf per size. The page needs all of it to decide
 * what to render, and a call per product is how a screen becomes slow
 * enough to feel broken.
 *
 * Honey is deliberately absent from the response. It has its own front
 * door and always has; #402 and #410 just finished simplifying the
 * Honey surfaces, and piping wax through them would undo that.
 */
export const productsApi = {
  list: () => apiClient.get("/products").then((r) => r.data),
};
